import React, { useMemo } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Container,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import HeliosTitle from './HeliosTitle';
import { colors, spacing, appBarStyles, pageLayoutDefaults } from '../../shared/styles/theme';
import { useMe } from '../hooks/useMe';
import { useSidebarChrome } from '../context/SidebarChromeContext';
import { getVisibleNavSections } from '../navigation/appNavConfig';
import {
  AppDrawerShell,
  APP_DRAWER_WIDTH,
  APP_DRAWER_WIDTH_COLLAPSED,
} from './AppSidebar';

interface PageLayoutProps {
  children: React.ReactNode;
  rightActions?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  contentPaddingTop?: string;
  showContainer?: boolean;
}

const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  rightActions,
  maxWidth = pageLayoutDefaults.maxWidth,
  contentPaddingTop = pageLayoutDefaults.contentPaddingTop,
  showContainer = true,
}) => {
  const { me } = useMe();
  const {
    mobileNavOpen,
    setMobileNavOpen,
    closeMobileNav,
    desktopCollapsed,
    toggleDesktopCollapsed,
  } = useSidebarChrome();
  const navSections = getVisibleNavSections({ me });

  const desktopDrawerWidth = useMemo(
    () => (desktopCollapsed ? APP_DRAWER_WIDTH_COLLAPSED : APP_DRAWER_WIDTH),
    [desktopCollapsed],
  );

  const mainContent = showContainer ? (
    <Container maxWidth={maxWidth} sx={{ pt: contentPaddingTop, pb: spacing.container.paddingBottom }}>
      {children}
    </Container>
  ) : (
    <Box sx={{ pt: contentPaddingTop, pb: spacing.container.paddingBottom }}>{children}</Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: colors.background.page }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          ...appBarStyles,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: { md: `calc(100% - ${desktopDrawerWidth}px)` },
          ml: { md: `${desktopDrawerWidth}px` },
          transition: (theme) =>
            theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: desktopCollapsed
                ? theme.transitions.duration.leavingScreen
                : theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Toolbar sx={{ px: 3, py: spacing.header.paddingY, minHeight: spacing.header.minHeight }}>
          <IconButton
            color="inherit"
            aria-label="Open navigation menu"
            edge="start"
            onClick={() => setMobileNavOpen(true)}
            sx={{ mr: 2, display: { md: 'none' }, color: colors.primaryLight }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <HeliosTitle />
          </Box>

          {rightActions ? (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>{rightActions}</Box>
          ) : null}
        </Toolbar>
      </AppBar>

      <AppDrawerShell
        open={mobileNavOpen}
        onClose={closeMobileNav}
        sections={navSections}
        onNavigate={closeMobileNav}
        desktopDrawerWidth={desktopDrawerWidth}
        desktopCollapsed={desktopCollapsed}
        onToggleDesktopCollapsed={toggleDesktopCollapsed}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${desktopDrawerWidth}px)` },
          minWidth: 0,
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: desktopCollapsed
                ? theme.transitions.duration.leavingScreen
                : theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Toolbar sx={{ minHeight: spacing.header.minHeight }} />
        {mainContent}
      </Box>
    </Box>
  );
};

export default PageLayout;
